'use client';

import { useMemo } from 'react';
import { Hourglass, Sparkles } from 'lucide-react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogFooter,
	AlertDialogTitle,
	AlertDialogDescription
} from '../UIComponents/AlertDialog';
import { Button } from '../UIComponents/Button';

/**
 * Dialog shown when user exceeds their token limit
 */
interface TokenLimitDialogProps {
	isOpen: boolean;
	resetAt: string | null;
	onClose: () => void;
}

export function TokenLimitDialog({ isOpen, resetAt, onClose }: TokenLimitDialogProps) {
	// Format the reset time in user's local timezone
	const formattedResetTime = useMemo(() => {
		if (!resetAt) return null;

		try {
			const resetDate = new Date(resetAt);
			const timeStr = resetDate.toLocaleString(undefined, {
				hour: 'numeric',
				minute: '2-digit',
				hour12: true
			});
			const dateStr = resetDate.toLocaleString(undefined, {
				weekday: 'short',
				day: 'numeric',
				month: 'short'
			});
			return { time: timeStr, date: dateStr };
		} catch {
			return null;
		}
	}, [resetAt]);

	return (
		<AlertDialog
			open={isOpen}
			onOpenChange={onClose}
		>
			<AlertDialogContent className='border-border bg-background/95 max-w-md gap-6 p-8 shadow-2xl backdrop-blur-xl sm:rounded-2xl'>
				<AlertDialogHeader className='flex flex-col items-center gap-4 text-center sm:text-center'>
					{/* Icon Container with Glow Effect */}
					<div className='bg-card ring-foreground/10 relative flex h-16 w-16 items-center justify-center rounded-2xl shadow-inner ring-1'>
						<div className='absolute -inset-4 animate-pulse rounded-full bg-amber-500/10 blur-xl' />
						<Hourglass className='relative h-8 w-8 text-amber-500' />
						<div className='bg-background ring-background absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full ring-4'>
							<Sparkles className='h-3.5 w-3.5 text-amber-500' />
						</div>
					</div>

					<div className='space-y-2'>
						<AlertDialogTitle className='text-foreground text-2xl font-bold tracking-tight'>
							Limit Reached
						</AlertDialogTitle>
						<AlertDialogDescription className='text-muted-foreground text-base'>
							You&apos;ve used all your AI tokens for this window.
						</AlertDialogDescription>
					</div>
				</AlertDialogHeader>

				{formattedResetTime && (
					<div className='border-border bg-card/50 flex flex-col items-center justify-center gap-1.5 rounded-xl border p-4 text-center'>
						<span className='text-muted-foreground text-xs font-medium tracking-wider uppercase'>Tokens Reset At</span>
						<div className='flex items-baseline gap-2'>
							<span className='text-foreground text-2xl font-bold'>{formattedResetTime.time}</span>
							<span className='text-muted-foreground text-sm'>on {formattedResetTime.date}</span>
						</div>
					</div>
				)}

				<AlertDialogFooter className='sm:justify-center'>
					<AlertDialogPrimitive.Action asChild>
						<Button
							size='lg'
							className='bg-foreground text-background shadow-foreground/10 hover:bg-foreground/90 w-full font-semibold shadow-lg sm:w-auto sm:min-w-32'
							onClick={onClose}
						>
							Got it
						</Button>
					</AlertDialogPrimitive.Action>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
